/**
 * Trade alert system — desktop notifications + sound alerts
 */

// Sound URLs (short beeps, no external dependency)
const SOUNDS = {
  trade: null,   // will be created on first use
  exit: null,
  signal: null,
};

function _createBeep(frequency, duration, type = "sine") {
  return () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.value = frequency;
      gain.gain.value = 0.7;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.stop(ctx.currentTime + duration);
    } catch {}
  };
}

// Different sounds for different events
const playTradeSound = () => {                                // Triple beep — trade placed (loud)
  _createBeep(880, 0.25, "square")();
  setTimeout(() => _createBeep(1100, 0.25, "square")(), 300);
  setTimeout(() => _createBeep(1320, 0.35, "square")(), 600);
};
const playExitSound = _createBeep(440, 0.5, "triangle");    // Low tone — trade closed
const playSignalSound = _createBeep(660, 0.15, "square");   // Quick chirp — new signal
const playSLSound = _createBeep(330, 0.6, "sawtooth");      // Low buzz — stop loss hit
const playTargetSound = () => {                               // Double beep — target hit
  _createBeep(880, 0.15, "sine")();
  setTimeout(() => _createBeep(1100, 0.2, "sine")(), 200);
};

/**
 * Request notification permission (call once on app load)
 */
export function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission();
  }
}

/**
 * Show desktop notification
 */
function notify(title, body, tag) {
  if ("Notification" in window && Notification.permission === "granted") {
    try {
      new Notification(title, {
        body,
        tag: tag || "trade-alert",
        icon: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>📊</text></svg>",
        requireInteraction: false,
        silent: true, // we handle our own sound
      });
    } catch {}
  }
}

/**
 * Alert: Trade placed (simulate or live)
 */
export function alertTradePlaced(trade) {
  const mode = trade.simulated ? "SIM" : "LIVE";
  const type = trade.type === "LONG" ? "CALL" : "PUT";
  const title = `${mode} Trade: ${trade.type} ${type}`;
  const body = `Entry: ${trade.entry?.toFixed(1)} | SL: ${trade.sl?.toFixed(1)} | Target: ${trade.target?.toFixed(1)}\nStrike: ${trade.signalType} ${trade.strike} | Qty: ${trade.qty}`;

  notify(title, body, `trade-${trade.id}`);
  playTradeSound();
}

/**
 * Alert: Trade closed (SL, target, trailing, manual)
 */
export function alertTradeClosed(trade) {
  const reason = trade.exitReason || "CLOSED";
  const pnl = trade.netPnl != null ? trade.netPnl : trade.grossPnl;
  const pnlStr = pnl != null ? `${pnl >= 0 ? "+" : ""}₹${Math.abs(pnl).toLocaleString("en-IN")}` : "";
  const title = `Trade Closed: ${reason}`;
  const body = `${trade.type} ${trade.signalType} ${trade.strike} | ${pnlStr}\nEntry: ${trade.entry?.toFixed(1)} → Exit: ${trade.exitPrice?.toFixed(1) || "?"}`;

  notify(title, body, `exit-${trade.id}`);

  if (reason.includes("TARGET")) {
    playTargetSound();
  } else if (reason.includes("STOP LOSS")) {
    playSLSound();
  } else {
    playExitSound();
  }
}

/**
 * Alert: New signal detected (optional, for scan-only mode)
 */
export function alertSignalDetected(signal, strategy) {
  const type = signal.type === "LONG" ? "CALL" : "PUT";
  const title = `Signal: ${signal.type} ${type} (${strategy})`;
  const body = `Entry: ${signal.entryPrice?.toFixed(1)} | SL: ${signal.stopLoss?.toFixed(1)} | Risk: ${signal.risk?.toFixed(1)}`;

  notify(title, body, `signal-${Date.now()}`);
  playSignalSound();
}

/**
 * Alert: Missed opportunity
 */
export function alertMissed(signal, reason) {
  const title = `Missed: ${signal.type} @ ${signal.entryPrice?.toFixed(1)}`;
  const body = `Reason: ${reason}`;
  notify(title, body, `missed-${Date.now()}`);
  // No sound for missed — just notification
}
